import { Card } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  LoaderIcon,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";
import ReactApexChart from "react-apexcharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { productAnalyticsQueries } from "@/api/product-analytics";
import { DateRange } from "@/lib/date";
import { useMemo, useState } from "react";
import { productQueries } from "@/api/product";
import { safeParseDate } from "@/lib/utils";
import { ApexOptions } from "apexcharts";

const CHART_OPTIONS = {
  chart: {
    type: "line",
    toolbar: { show: false },
    zoom: { enabled: false },
  },
  xaxis: {
    type: "datetime",
    labels: { format: "MMM dd" },
  },
  yaxis: {
    labels: {
      formatter: (val: number) => val.toLocaleString(),
    },
    title: { text: "Credits Used" },
  },
  tooltip: {
    x: { format: "MMM dd, yyyy" },
    y: {
      formatter: (val: number) => val.toLocaleString(),
    },
  },
  stroke: { curve: "smooth", width: 2 },
  legend: { show: true, position: "top" },
} satisfies ApexOptions;

export function ProductComparison({
  productId,
  dateRange,
}: {
  productId: string;
  dateRange: DateRange;
}) {
  const [comparisonProducts, setComparisonProducts] = useState<string[]>([]);

  const { data: comparisonData, isLoading: isComparisonLoading } = useQuery({
    ...productAnalyticsQueries.comparison(
      [productId, ...comparisonProducts].filter(Boolean),
      dateRange.from?.toISOString(),
      dateRange.to?.toISOString(),
    ),
  });

  const { data: allProducts = [] } = useQuery(productQueries.products());

  const handleAddComparison = (pid: string) => {
    if (!comparisonProducts.includes(pid)) {
      setComparisonProducts((prev) => [...prev, pid]);
    }
  };

  const handleRemoveComparison = (pid: string) => {
    setComparisonProducts((prev) => prev.filter((x) => x !== pid));
  };

  const comparisonSeries = useMemo(() => {
    if (!comparisonData) return [];
    return Object.entries(comparisonData).map(([productId, data], idx) => {
      const p = allProducts.find((x) => x.product_sku === productId);
      const name = p ? p.name : productId;
      // @ts-expect-error not sure if property exists
      const daily = data.daily_usage ?? [];
      return {
        name,
        data: daily.map((d: any) => [
          safeParseDate(d.date)?.getTime() ?? d.date,
          d.credits ?? 0,
        ]),
        color: ["#D1FA64", "#8884d8", "#82ca9d", "#ffc658"][idx] || "#666",
      };
    });
  }, [comparisonData, allProducts]);

  return (
    <>
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Product Comparison</h3>
            <p className="text-sm text-muted-foreground">
              Compare metrics across different products
            </p>
          </div>
          <div className="flex items-center gap-2">
            {comparisonProducts.length < 3 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline">Add Product</Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                  <Command>
                    <CommandInput placeholder="Search products..." />
                    <CommandList>
                      <CommandEmpty>No products found</CommandEmpty>
                      <CommandGroup>
                        {allProducts
                          .filter(
                            (p) =>
                              p.product_sku !== productId &&
                              !comparisonProducts.includes(p.product_sku),
                          )
                          .map((p) => (
                            <CommandItem
                              key={p.product_sku}
                              onSelect={() =>
                                handleAddComparison(p.product_sku)
                              }
                            >
                              {p.name}
                              <Badge className="ml-2" variant="outline">
                                {p.product_family}
                              </Badge>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Selected comparisons */}
        <div className="flex gap-2 flex-wrap">
          {[productId, ...comparisonProducts].map((pid) => {
            const p = allProducts.find((x) => x.product_sku === pid);
            return (
              p && (
                <Badge key={pid} variant="secondary" className="py-2 px-3">
                  {p.name}
                  {pid !== productId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 ml-2"
                      onClick={() => handleRemoveComparison(pid)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </Badge>
              )
            );
          })}
        </div>
      </Card>

      {isComparisonLoading ? (
        <div className="h-[200px] flex items-center justify-center">
          <LoaderIcon className="h-8 w-8 animate-spin" />
        </div>
      ) : comparisonData ? (
        <>
          {/* Comparison Chart */}
          <Card className="p-4 mb-4">
            <h3 className="text-lg font-semibold mb-4">Usage Comparison</h3>
            {/* Build an apex chart from your “comparisonSeries” */}
            {comparisonSeries.length > 0 ? (
              <ReactApexChart
                type="line"
                series={comparisonSeries.map((s) => ({
                  name: s.name,
                  data: s.data,
                  color: s.color,
                }))}
                options={CHART_OPTIONS}
                height={400}
              />
            ) : (
              <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                <AlertCircle className="h-8 w-8 mr-2" />
                No comparison data
              </div>
            )}
          </Card>

          {/* Comparison table with metrics */}
          <Card className="p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric</TableHead>
                  {Object.keys(comparisonData).map((pid) => {
                    const p = allProducts.find((x) => x.product_sku === pid);
                    return <TableHead key={pid}>{p?.name || pid}</TableHead>;
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>Total Credits</TableCell>
                  {Object.values(comparisonData).map((dat, i) => {
                    const c = dat.summary.total_credits ?? 0;
                    return (
                      <TableCell key={i} className="font-mono">
                        {c.toLocaleString()}
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableCell>Total Calls</TableCell>
                  {Object.values(comparisonData).map((dat, i) => {
                    const calls = dat.summary.total_calls ?? 0;
                    return (
                      <TableCell key={i} className="font-mono">
                        {calls.toLocaleString()}
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableCell>Avg Credits/Call</TableCell>
                  {Object.values(comparisonData).map((dat, i) => {
                    const avg = dat.summary.average_credits_per_call ?? 0;
                    return (
                      <TableCell key={i} className="font-mono">
                        {avg.toFixed(2)}
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableCell>Growth Rate</TableCell>
                  {Object.values(comparisonData).map((dat, i) => {
                    const gr = dat.summary.growth_rate ?? 0;
                    return (
                      <TableCell key={i}>
                        <div className="flex items-center gap-1">
                          {gr >= 0 ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          )}
                          <span
                            className={
                              gr >= 0 ? "text-green-500" : "text-red-500"
                            }
                          >
                            {Math.abs(gr).toFixed(1)}%
                          </span>
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </>
      ) : (
        <div className="h-[200px] flex items-center justify-center text-muted-foreground">
          <AlertCircle className="h-8 w-8 mr-2" />
          No comparison data
        </div>
      )}
    </>
  );
}
